import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import ProgressMeter from "../components/ProgressMeter";
import ResmoMascot from "../components/ResmoMascot";
import ResultSection from "../components/ResultSection";
import { ReportTabStrip } from "../components/reports/ScoreAnalysis";
import SectionExpandToolbar from "../components/SectionExpandToolbar";
import {
  RK, ReportLabel, ReportList, ReportRichList, ReportGrid, ReportChips,
  ReportCallout, ReportStat,
} from "../components/reports/ReportKit";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  ArrowLeft, ArrowRight, Briefcase, MapPin, Search, TrendingUp, GraduationCap,
  BookOpen, Award, Users, Loader, ExternalLink, Bot, Trash2,
  Share2, Link as LinkIcon, X, Copy, Check, Lock, DollarSign, Clock, Wand2,
} from "lucide-react";

const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR"];

export function RoleResearchList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/role-research");
      setItems(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this research?")) return;
    try { await api.delete(`/role-research/${id}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  if (loading) return <div className="font-mono-rm text-sm text-center py-6">Loading...</div>;
  if (error) return <div className="font-mono-rm text-sm">{error}</div>;
  if (items.length === 0) return null;

  return (
    <div className="mt-6" data-testid="role-research-list">
      <div className="rm-label mb-3">Recent role research</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const demandColor = ({ hot: "#EF4444", strong: "#20C1A7", steady: "#FFC145", declining: "#94A3B8" })[it.demand] || "#94A3B8";
          return (
            <div key={it.id} className="rm-card p-5" data-testid={`role-card-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg uppercase leading-tight truncate">{it.role}</div>
                  <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
                    {it.location || "—"}
                  </div>
                </div>
                <span className="rm-tag flex-shrink-0" style={{ background: demandColor, color: "white", borderColor: "#0F1B2D" }}>
                  {it.demand || "—"}
                </span>
              </div>
              {it.mid_salary && (
                <div className="mt-2 font-mono-rm text-sm">
                  mid: {it.currency} {Math.round(it.mid_salary).toLocaleString()}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Link to={`/skills/role/${it.id}`} className="rm-btn rm-btn-primary text-xs px-3 py-2 flex-1" data-testid={`view-role-${it.id}`}>
                  Open
                </Link>
                <button onClick={() => handleDelete(it.id)} className="rm-btn rm-btn-ghost text-xs px-3 py-2">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function RoleResearchWizard() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const presetRole = (() => {
    try {
      return new URLSearchParams(routerLocation.search).get("role") || "";
    } catch { return ""; }
  })();
  const [role, setRole] = useState(presetRole);
  const [location, setLocation] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [submitting, setSubmitting] = useState(false);
  const [stage, setStage] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!submitting) return;
    const start = Date.now();
    setElapsed(0);
    const t = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(t);
  }, [submitting]);

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!role.trim()) {
      setError("Tell Resmo which role to research.");
      return;
    }
    setSubmitting(true);
    setStage("queued");
    try {
      const { data: jobData } = await api.post("/role-research", { role, location, currency });
      const jobId = jobData.job_id;
      const pollUntil = Date.now() + 1000 * 60 * 4;
      while (Date.now() < pollUntil) {
        await new Promise((r) => setTimeout(r, 2500));
        try {
          const { data: status } = await api.get(`/jobs/${jobId}`);
          if (status.stage) setStage(status.stage);
          if (status.status === "complete" && status.research_id) {
            navigate(`/skills/role/${status.research_id}`);
            return;
          }
          if (status.status === "error") {
            setSubmitting(false);
            setError(status.error || "Research failed.");
            return;
          }
        } catch { /* keep polling */ }
      }
      setSubmitting(false);
      setError("Resmo took too long. Try again.");
    } catch (e) {
      setSubmitting(false);
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (submitting) {
    const stageMap = { queued: "Surveying the field", analyzing: "Gathering market intel", saving: "Plating your brief", complete: "Done!" };
    return (
      <div className="text-center py-16 max-w-2xl mx-auto" data-testid="role-research-submitting">
        <ResmoMascot state="chewing" size={220} />
        <div className="font-display text-3xl uppercase mt-6">
          Researching <span className="rm-text-orange">{role}</span>...
        </div>
        <div className="font-mono-rm text-sm mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
          {stageMap[stage] || "Processing..."}
        </div>
        <div className="font-display text-2xl mt-4" data-testid="role-research-progress-percent">
          {Math.min(95, Math.round(8 + elapsed * 2.5))}<span className="text-base" style={{ color: "#475569" }}>% complete</span>
        </div>
        <div className="mt-4 h-3 rounded-full overflow-hidden mx-auto max-w-sm" style={{ background: "#FFF2EC", border: "2px solid #0F1B2D" }}>
          <div className="h-full transition-all" style={{ width: `${Math.min(95, 8 + elapsed * 2.5)}%`, background: "#FF6A00" }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <button onClick={() => navigate("/skills")} className="rm-tag mb-3" data-testid="role-wizard-back-btn">
        <ArrowLeft size={14} /> Skill Lab
      </button>
      <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">
        Research a <span className="rm-text-orange">role.</span>
      </h1>
      <p className="text-base md:text-lg max-w-2xl mb-8" style={{ color: "#475569" }}>
        Give Resmo a job title. Get back what the role really does, skills it demands,
        salary by experience tier, and a roadmap to grow into it.
      </p>

      <form onSubmit={submit} className="rm-card-static p-6 md:p-8 space-y-5">
        <div>
          <label className="rm-label block mb-2"><Briefcase size={12} className="inline mr-1" /> Role / occupation *</label>
          <input
            className="rm-input"
            placeholder="Database Administrator, Data Engineer, UX Researcher..."
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="role-input"
            autoFocus
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="rm-label block mb-2"><MapPin size={12} className="inline mr-1" /> Location (optional)</label>
            <input
              className="rm-input"
              placeholder="San Francisco, Remote US, London..."
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="role-location-input"
            />
          </div>
          <div>
            <label className="rm-label block mb-2">Currency</label>
            <select className="rm-input" value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="role-currency-select">
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {error && (
          <div className="p-3 rounded-md font-mono-rm text-sm" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="role-error">
            {error}
          </div>
        )}

        <div className="flex justify-end">
          <button type="submit" className="rm-btn rm-btn-primary" data-testid="role-submit-btn">
            <Search size={16} /> Research this role <ArrowRight size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}

function searchUrl(query) {
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
}

export function RoleResearchResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [certsLoading, setCertsLoading] = useState(false);
  const [certsError, setCertsError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/role-research/${id}`);
        setData(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id]);

  const handleCreateShare = async () => {
    setShareLoading(true);
    try {
      const { data } = await api.post(`/role-research/${id}/share`);
      setShareUrl(data.share_url);
      setShowShare(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    try {
      await api.delete(`/role-research/${id}/share`);
      setShareUrl("");
      setShowShare(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const handleGenerateCerts = async () => {
    setCertsError("");
    setCertsLoading(true);
    try {
      const { data: resp } = await api.post(`/role-research/${id}/certifications`);
      setData((prev) => ({ ...prev, certifications: resp.certifications }));
    } catch (e) {
      setCertsError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCertsLoading(false);
    }
  };

  const handleCopyShare = async () => {
    if (!shareUrl) return;
    await navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  const tierWidth = useMemo(() => {
    if (!data) return 0;
    const tiers = data.result?.salary_tiers || [];
    const max = Math.max(...tiers.map((t) => t.high || 0), 1);
    return max;
  }, [data]);

  if (error) {
    return (
      <div className="text-center py-16">
        <ResmoMascot state="hungry" size={160} />
        <div className="font-display text-2xl mt-3 uppercase">Couldn't load that research</div>
        <div className="font-mono-rm text-sm mt-2">{error}</div>
        <button onClick={() => navigate("/skills")} className="rm-btn rm-btn-primary mt-6">Back</button>
      </div>
    );
  }
  if (!data) return <div className="text-center py-16 font-mono-rm">Loading...</div>;

  const r = data.result || {};
  const overview = r.role_overview || {};
  const skills = r.skills_required || {};
  const tiers = r.salary_tiers || [];
  const career = r.career_path || {};
  const edu = r.education || {};
  const outlook = r.market_outlook || {};
  const certifications = data.certifications || null;

  const fmt = (n) => n ? `${data.currency} ${Math.round(n).toLocaleString()}` : "—";
  const demandColor = ({ hot: "#EF4444", strong: "#20C1A7", steady: "#FFC145", declining: "#94A3B8" })[outlook.demand] || "#94A3B8";

  return (
    <div>
      <button onClick={() => navigate("/skills")} className="rm-tag mb-3" data-testid="role-result-back-btn">
        <ArrowLeft size={14} /> Skill Lab
      </button>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="rm-tag mb-2">Role Brief</div>
          <h1 className="font-display text-4xl md:text-5xl uppercase">{data.role}</h1>
          <div className="font-mono-rm text-sm mt-2 uppercase tracking-widest" style={{ color: "#475569" }}>
            {data.location || "U.S. national average"}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleCreateShare}
            disabled={shareLoading}
            className="rm-btn rm-btn-secondary text-xs px-3 py-2"
            data-testid="role-share-btn"
            title="Share this role brief"
          >
            <Share2 size={14} /> {shareLoading ? "Loading..." : "Share"}
          </button>
          <span className="rm-tag" style={{ background: demandColor, color: "white", borderColor: "#0F1B2D" }}>
            {outlook.demand || "—"} demand
          </span>
          <ResmoMascot state="satisfied" size={110} />
        </div>
      </div>

      {/* Stacked collapsible sections — replaced the older tab strip so users
          can see all the parts of their role brief at a glance and drill in
          without losing context. Day-in-the-Life opens by default. */}
      <SectionExpandToolbar
        group="role-result"
        targetSelector='[data-testid="role-sections"]'
        pdfTitle={`Role brief · ${data.role || "Untitled role"}`}
        pdfFilename={`resmo-role-${(data.role || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`}
      />
      <ReportTabStrip
        testIdPrefix="role-tab"
        tabs={[
          { id: "day", label: "Day-in-the-Life", sectionTestId: "role-section-day" },
          { id: "comp", label: "Comp & Ladders", sectionTestId: "role-section-comp" },
          { id: "breakin", label: "Break-In", sectionTestId: "role-section-breakin" },
          { id: "career", label: "Career Path", sectionTestId: "role-section-career" },
          { id: "edu", label: "Education", sectionTestId: "role-section-edu" },
          { id: "certs", label: "Certifications", sectionTestId: "role-section-certs" },
          { id: "outlook", label: "Market Outlook", sectionTestId: "role-section-outlook" },
        ]}
      />
      <div className="space-y-3" data-testid="role-sections">

      <ResultSection
        title="Day-in-the-Life"
        eyebrow="What this role actually does"
        accentGradient="linear-gradient(135deg,#FBBF24 0%,#CA8A04 100%)"
        defaultOpen
        group="role-result"
        testid="role-section-day"
      >
      <div data-testid="role-overview-card">
        <ReportLabel color={RK.navy} className="mb-1">What this role does</ReportLabel>
        <p className="text-[13px] leading-snug mb-4" style={{ color: RK.body }}>{overview.summary || "—"}</p>
        <ReportChips title="Also called" items={overview.typical_titles} tone="nice" className="mb-4 block" />
        <ReportGrid>
          <ReportList title="Day-to-day" color={RK.orange} items={overview.day_to_day} />
          <ReportList title="Top industries" color={RK.amber} items={overview.industries} />
        </ReportGrid>
      </div>
      </ResultSection>

      <ResultSection
        title="Comp & Ladders"
        eyebrow="Salary by experience + where this can take you"
        accentGradient="linear-gradient(135deg,#0F1B2D 0%,#1E3A5F 100%)"
        group="role-result"
        testid="role-section-comp"
      >
      <div className="rounded-md p-4 md:p-5 mb-1" style={{ background: "#0F1B2D", color: "white", border: "1.5px solid #0F1B2D" }} data-testid="salary-tiers-card">
        <ReportLabel color={RK.mustard} className="mb-4">Salary by experience — from entry to expert</ReportLabel>
        <div className="space-y-3">
          {tiers.map((t, i) => {
            const pct = tierWidth ? Math.max(8, (t.high / tierWidth) * 100) : 0;
            const lowPct = tierWidth ? (t.low / tierWidth) * 100 : 0;
            return (
              <div key={i} className="p-4 rounded-md" style={{ background: "rgba(255,255,255,0.05)", border: "2px solid #20C1A7" }} data-testid={`tier-${t.tier}`}>
                <div className="flex justify-between items-baseline gap-3 mb-2 flex-wrap">
                  <div className="font-display text-lg uppercase">
                    {t.tier} <span className="font-mono-rm text-xs" style={{ color: "#94A3B8" }}>· {t.years} yrs</span>
                  </div>
                  <div className="font-mono-rm text-sm">
                    <span style={{ color: "#94A3B8" }}>{fmt(t.low)}</span> – <strong style={{ color: "#FF6A00" }}>{fmt(t.mid)}</strong> – <span style={{ color: "#94A3B8" }}>{fmt(t.high)}</span>
                  </div>
                </div>
                <div className="relative h-3 rounded-full" style={{ background: "rgba(255,255,255,0.1)", border: "1px solid #20C1A7" }}>
                  <div className="absolute h-full rounded-full" style={{
                    left: `${lowPct}%`, width: `${pct - lowPct}%`,
                    background: "linear-gradient(90deg, #FF6A00, #FFC145)",
                  }} />
                </div>
                {t.what_changes && (
                  <p className="text-sm mt-2" style={{ color: "#CBD5E1" }}>{t.what_changes}</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
      </ResultSection>

      <ResultSection
        title="Break-In"
        eyebrow="Skills + how to skill up"
        accentGradient="linear-gradient(135deg,#F97316 0%,#B91C1C 100%)"
        group="role-result"
        testid="role-section-breakin"
      >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-1">
        <ReportRichList
          title="Must-have skills — non-negotiables"
          color={RK.orange}
          items={(skills.must_have || []).map((s) => ({ heading: s.name, body: s.why }))}
          testId="must-have-card"
        />
        <ReportRichList
          title="Nice-to-have — edge-makers"
          color={RK.slate}
          items={(skills.nice_to_have || []).map((s) => ({ heading: s.name, body: s.why }))}
          testId="nice-to-have-card"
        />
        <ReportChips title="Soft skills" items={skills.soft_skills} tone="nice" testId="soft-skills-card" />
        <ReportChips title="Tools & platforms" items={skills.tools} tone="teal" testId="tools-card" />
      </div>
      </ResultSection>

      <ResultSection
        title="Career Path"
        eyebrow="Where this role can take you"
        accentGradient="linear-gradient(135deg,#3B82F6 0%,#1E40AF 100%)"
        group="role-result"
        testid="role-section-career"
      >
      <div data-testid="career-path-card">
        <ReportGrid cols={3}>
          <ReportList title="Progression ladder" color={RK.blue} ordered items={career.typical_progression} />
          <ReportList title="Lateral moves" color={RK.navy} items={career.lateral_options} />
          <ReportList title="Exit opportunities" color={RK.green} items={career.exit_opportunities} />
        </ReportGrid>
      </div>
      </ResultSection>

      <ResultSection
        title="Education & Resources"
        eyebrow="Build the credentials"
        accentGradient="linear-gradient(135deg,#10B981 0%,#047857 100%)"
        group="role-result"
        testid="role-section-edu"
      >
      <div data-testid="education-card">
        <ReportGrid>
          <ReportList title="Degrees" icon={<GraduationCap size={11} />} color={RK.blue} items={edu.degrees} />
          <ReportList
            title="Certifications"
            icon={<Award size={11} />}
            color={RK.green}
            items={edu.certifications}
            renderItem={(c, i) => (
              <>
                <a href={searchUrl(`${c.name} ${c.provider}`)} target="_blank" rel="noopener noreferrer" className="underline font-semibold" style={{ color: RK.navy }} data-testid={`cert-link-${i}`}>
                  {c.name}
                </a>
                <span style={{ color: RK.sub }}> · {c.provider}{c.level ? ` · ${c.level}` : ""}</span>
                <ExternalLink size={11} className="inline ml-1" />
              </>
            )}
          />
          <ReportList
            title="Online courses"
            icon={<BookOpen size={11} />}
            color={RK.purple}
            items={edu.courses}
            renderItem={(c, i) => (
              <>
                <a href={searchUrl(`${c.name} ${c.provider}`)} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: RK.navy }} data-testid={`course-link-${i}`}>
                  {c.name}
                </a>
                <span style={{ color: RK.sub }}> · {c.provider}</span>
                <ExternalLink size={11} className="inline ml-1" />
              </>
            )}
          />
          <ReportList
            title="Books"
            color={RK.amber}
            items={edu.books}
            renderItem={(bk) => (
              <>
                <a href={searchUrl(`${bk.title} ${bk.author} book`)} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: RK.navy }}>
                  {bk.title}
                </a>
                {bk.author && <span style={{ color: RK.sub }}> · {bk.author}</span>}
              </>
            )}
          />
          <ReportChips title="Communities" icon={<Users size={11} />} items={edu.communities} tone="trending" className="md:col-span-2" testId="edu-communities" />
        </ReportGrid>
      </div>
      </ResultSection>

      <ResultSection
        title="Certifications"
        eyebrow="Worth your money?"
        accentGradient="linear-gradient(135deg,#FFC145 0%,#B45309 100%)"
        group="role-result"
        testid="role-section-certs"
      >
        <div data-testid="certs-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <ReportLabel icon={<Award size={11} />} color={RK.amber}>Ranked by real-world ROI</ReportLabel>
            {certifications && (
              <button
                onClick={handleGenerateCerts}
                disabled={certsLoading}
                className="rm-btn rm-btn-ghost text-xs px-3 py-2"
                data-testid="certs-refresh-btn"
              >
                {certsLoading ? <Loader size={12} className="animate-spin" /> : <Wand2 size={12} />}
                {certsLoading ? "Refreshing..." : "Refresh"}
              </button>
            )}
          </div>

          {certsError && (
            <div
              className="mb-4 p-3 rounded font-mono-rm text-sm"
              style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
              data-testid="certs-error"
            >
              {certsError}
            </div>
          )}

          {!certifications && (
            <div className="text-center py-8" data-testid="certs-empty">
              <Award size={48} style={{ color: "#FFC145" }} className="mx-auto mb-3" strokeWidth={2.5} />
              <h3 className="font-display text-xl uppercase mb-2">
                Which certifications actually move the needle?
              </h3>
              <p className="text-sm max-w-lg mx-auto mb-5" style={{ color: "#475569" }}>
                Resmo will rank certifications for <strong>{data.role}</strong> by tier, with real
                costs, time investment, expected salary impact, and a step-by-step prep path. We
                only name credentials that actually exist.
              </p>
              <button
                onClick={handleGenerateCerts}
                disabled={certsLoading}
                className="rm-btn rm-btn-primary"
                data-testid="certs-generate-btn"
              >
                {certsLoading ? <Loader size={16} className="animate-spin" /> : <Award size={16} />}
                {certsLoading ? "Resmo is digging..." : "Generate cert ranking (3 credits)"}
              </button>
              {certsLoading && (
                <div className="mt-3 max-w-sm">
                  <ProgressMeter expectedSeconds={25} label="Ranking certifications" testid="certs-progress-meter" />
                </div>
              )}
            </div>
          )}

          {certifications && (
            <>
              {certifications.summary && (
                <ReportCallout title="TL;DR" className="mb-4" testId="certs-summary">
                  {certifications.summary}
                </ReportCallout>
              )}

              <div className="space-y-3" data-testid="certs-list">
                {(certifications.certifications || []).map((c, i) => (
                  <CertCard key={c.name || c.title || i} cert={c} index={i} />
                ))}
              </div>

              {(certifications.tips || []).length > 0 && (
                <ReportList title="Choosing wisely" color={RK.orange} items={certifications.tips} testId="certs-tips" className="mt-6 block" />
              )}
            </>
          )}
        </div>
      </ResultSection>

      <ResultSection
        title="Market Outlook"
        eyebrow="Where this is heading"
        accentGradient="linear-gradient(135deg,#EF4444 0%,#991B1B 100%)"
        group="role-result"
        testid="role-section-outlook"
      >
      <div data-testid="outlook-card">
        <ReportGrid>
          {outlook.trend && (
            <div>
              <ReportLabel icon={<TrendingUp size={11} />} color={RK.orange} className="mb-1">Trend</ReportLabel>
              <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{outlook.trend}</p>
            </div>
          )}
          {outlook.ai_impact && (
            <div>
              <ReportLabel icon={<Bot size={11} />} color={RK.purple} className="mb-1">AI impact</ReportLabel>
              <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{outlook.ai_impact}</p>
            </div>
          )}
          {outlook.competitiveness && (
            <div className="md:col-span-2">
              <ReportLabel color={RK.red} className="mb-1">Competitiveness</ReportLabel>
              <p className="text-[13px] leading-snug" style={{ color: RK.body }}>{outlook.competitiveness}</p>
            </div>
          )}
        </ReportGrid>
      </div>
      </ResultSection>

      </div>{/* /role-sections */}

      <div className="flex justify-center">
        <Link to="/skills/role/new" className="rm-btn rm-btn-secondary" data-testid="another-role-btn">
          Research another role
        </Link>
      </div>

      {/* Share modal */}
      {showShare && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(15, 27, 45, 0.55)" }}
          onClick={() => setShowShare(false)}
          data-testid="role-share-modal"
        >
          <div
            className="rm-card-static p-6 md:p-8 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            style={{ background: "#FFF2EC" }}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="rm-tag mb-2"><LinkIcon size={12} /> Share</div>
                <h2 className="font-display text-3xl uppercase leading-tight">
                  Share this <span className="rm-text-orange">role brief.</span>
                </h2>
                <p className="text-sm mt-2" style={{ color: "#475569" }}>
                  Anyone with this link can see the salary tiers, must-have skills, career path,
                  and market outlook for {data.role}. No personal data is shared.
                </p>
              </div>
              <button
                onClick={() => setShowShare(false)}
                className="rm-btn rm-btn-ghost text-xs px-2 py-2"
                data-testid="role-share-close-btn"
              >
                <X size={14} />
              </button>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="rm-input flex-1"
                data-testid="role-share-url-input"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopyShare}
                className="rm-btn rm-btn-primary text-xs px-3 py-2"
                data-testid="role-share-copy-btn"
              >
                {shareCopied ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy</>}
              </button>
            </div>
            <div className="flex justify-between items-center mt-4 flex-wrap gap-3">
              <a
                href={shareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono-rm text-xs uppercase tracking-widest underline"
                style={{ color: "#FF6A00" }}
                data-testid="role-share-preview-link"
              >
                Preview ↗
              </a>
              <button
                onClick={handleRevokeShare}
                className="rm-btn rm-btn-ghost text-xs"
                data-testid="role-share-revoke-btn"
              >
                Revoke link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const TIER_STYLE = {
  essential:    { bg: "#FEE2E2", color: "#991B1B", border: "#DC2626", label: "Essential" },
  strong:       { bg: "#FFFBEB", color: "#92400E", border: "#F59E0B", label: "Strong"    },
  "nice-to-have": { bg: "#F1F5F9", color: "#475569", border: "#94A3B8", label: "Nice to have" },
};

const DIFFICULTY_COLOR = {
  low:          "#10B981",
  medium:       "#F59E0B",
  high:         "#FF6A00",
  "very high":  "#DC2626",
};

function CertCard({ cert, index }) {
  const tierStyle = TIER_STYLE[(cert.tier || "").toLowerCase()] || TIER_STYLE["nice-to-have"];
  return (
    <div
      className="p-4 rounded-md"
      style={{ border: `1.5px solid ${tierStyle.border}`, background: "white", boxShadow: `2px 2px 0 0 ${tierStyle.border}33` }}
      data-testid={`cert-card-${index}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className="text-[10px] font-mono-rm uppercase tracking-widest px-2 py-0.5 rounded"
              style={{
                background: tierStyle.bg,
                color: tierStyle.color,
                border: `1.5px solid ${tierStyle.border}`,
                fontWeight: 700,
              }}
              data-testid={`cert-card-${index}-tier`}
            >
              · {tierStyle.label}
            </span>
            {cert.difficulty && (
              <span
                className="font-mono-rm text-[10px] uppercase tracking-widest"
                style={{ color: DIFFICULTY_COLOR[(cert.difficulty || "").toLowerCase()] || "#475569" }}
              >
                {cert.difficulty} difficulty
              </span>
            )}
          </div>
          <div className="font-display text-base uppercase leading-tight" data-testid={`cert-card-${index}-name`}>
            {cert.name}
          </div>
          {cert.issuer && (
            <div className="font-mono-rm text-xs uppercase tracking-widest mt-1" style={{ color: "#475569" }}>
              by {cert.issuer}
            </div>
          )}
        </div>
        {cert.official_url && (
          <a
            href={cert.official_url}
            target="_blank"
            rel="noopener noreferrer"
            className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
            data-testid={`cert-card-${index}-link`}
          >
            Official page <ExternalLink size={11} />
          </a>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <ReportStat label="Exam" icon={<DollarSign size={10} />} value={cert.exam_cost} />
        <ReportStat label="Prep" icon={<DollarSign size={10} />} value={cert.prep_cost_estimate} />
        <ReportStat label="Time" icon={<Clock size={10} />} value={cert.time_investment} />
        <ReportStat label="Renews" value={cert.recertification} />
      </div>

      {cert.salary_impact && (
        <ReportCallout
          title="Salary impact"
          color="#065F46"
          bg="#ECFDF5"
          border="#10B981"
          className="mb-3"
          testId={`cert-card-${index}-salary`}
        >
          {cert.salary_impact}
        </ReportCallout>
      )}

      {cert.best_for && (
        <div className="text-sm mb-3" style={{ color: "#475569" }}>
          <strong>Best for:</strong> {cert.best_for}
        </div>
      )}

      {(cert.prep_path || []).length > 0 && (
        <details className="mt-3" data-testid={`cert-card-${index}-prep-path`}>
          <summary className="cursor-pointer font-mono-rm text-xs uppercase tracking-widest" style={{ color: "#FF6A00" }}>
            Prep path ({cert.prep_path.length} steps)
          </summary>
          <ol className="mt-2 space-y-1 text-sm list-decimal ml-5">
            {cert.prep_path.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

